/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { GRAPH_MODES, INITIAL_GRAPH_STATE, VIEW_TYPES } from './graphConstants.js';
import { PROCESS_VIEW_PATH, SEARCH_PATH } from '../constants/routes.js';

// Constants
const MAX_TABS = 5;
const MAX_FULL_DATA_TABS = 5;
const TAB_WARNING_THRESHOLD = 4;
const MAX_TAB_DISPLAY_LENGTH = 34;

// Context
const TabContext = createContext(null);

/**
 * Generate a short display name for a tab
 * @param {string} searchTerm - The full search term
 * @returns {string} - Truncated display name
 */
const generateTabDisplayName = (searchTerm) => {
  if (!searchTerm) return 'New Tab';
  const trimmed = searchTerm.trim();
  if (trimmed.length <= MAX_TAB_DISPLAY_LENGTH) {
    return trimmed;
  }
  return `${trimmed.slice(0, MAX_TAB_DISPLAY_LENGTH)}...`;
};

const buildProcessViewSearchString = (searchTerm = '') => {
  const trimmed = typeof searchTerm === 'string' ? searchTerm.trim() : '';
  if (!trimmed) {
    return '';
  }

  const params = new URLSearchParams();
  params.set('query', trimmed);
  return params.toString();
};

const buildTabProcessViewPath = (tab) => {
  const normalizedSearchString = typeof tab?.processViewSearchString === 'string'
    ? tab.processViewSearchString.trim().replace(/^\?/, '')
    : '';

  if (normalizedSearchString) {
    return `${PROCESS_VIEW_PATH}?${normalizedSearchString}`;
  }

  const fallbackSearchString = buildProcessViewSearchString(tab?.searchTerm);
  return fallbackSearchString ? `${PROCESS_VIEW_PATH}?${fallbackSearchString}` : PROCESS_VIEW_PATH;
};

/**
 * Create a new tab object with default state
 * @param {string} searchTerm - The search term for this tab
 * @param {object} options - Additional options
 * @returns {object} - New tab object
 */
const createTabObject = (searchTerm = '', options = {}) => {
  const id = uuidv4();
  const now = Date.now();

  return {
    id,
    searchTerm,
    displayName: generateTabDisplayName(searchTerm),
    createdAt: now,
    lastAccessedAt: now,
    processViewSearchString: buildProcessViewSearchString(searchTerm),

    // Tab-specific graph state
    graphStates: {
      [GRAPH_MODES.BASIC]: { ...INITIAL_GRAPH_STATE },
      [GRAPH_MODES.ADVANCED]: { ...INITIAL_GRAPH_STATE }
    },
    currentMode: GRAPH_MODES.BASIC,
    currentViewType: VIEW_TYPES.GRAPH,
    graphKey: 0,

    // Search state
    searchValue: searchTerm,
    apiResponse: null,

    // Node state
    selectedNode: null,
    expandLevel: 0,
    panelOpen: false,

    // Filter state
    filters: [],
    selectedFiltersCount: 'ALL',
    nodeFilters: [],
    selectedNodeFiltersCount: 'ALL',
    isFilterPanelOpen: false,
    isNodeFilterPanelOpen: false,

    // AI state
    isLLMOutputLoading: false,
    expandAiAccordian: false,
    expandAiResponse: false,
    aiResponse: null,
    uuid: uuidv4(),
    graphUuid: uuidv4(),

    // UI state
    showTimeoutError: false,
    isAdminSpaceEnabled: false,

    // Viewport state for React Flow
    viewport: { x: 0, y: 0, zoom: 1 },

    // Memory management
    isCompressed: false,

    // Source tracking
    source: options.source || 'search', // 'search' | 'attribute-click' | 'session-summary'
    parentTabId: options.parentTabId || null,
  };
};

/**
 * Compress a tab to reduce memory footprint
 * @param {object} tab - Tab to compress
 * @returns {object} - Compressed tab with minimal data
 */
const compressTab = (tab) => {
  if (tab.isCompressed) return tab;

  return {
    ...tab,
    isCompressed: true,
    // Keep only essential data for re-fetching
    graphStates: {
      [GRAPH_MODES.BASIC]: { ...INITIAL_GRAPH_STATE },
      [GRAPH_MODES.ADVANCED]: { ...INITIAL_GRAPH_STATE }
    },
    apiResponse: null,
    aiResponse: null,
    selectedNode: null,
    // Preserve these for restoration
    searchTerm: tab.searchTerm,
    filters: tab.filters,
    nodeFilters: tab.nodeFilters,
    viewport: tab.viewport,
  };
};

// Custom hook
export const useTab = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTab must be used within a TabProvider');
  }
  return context;
};

export const TabProvider = ({ children }) => {
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  // Flag that indicates a tab-warning was triggered by a user attempting to CREATE a new tab
  const [showTabWarningOnCreate, setShowTabWarningOnCreate] = useState(false);
  const tabWarningDismissedRef = useRef(false);

  // Callback ref for saving graph state before tab switch (set by MultiTabGraphContainer)
  const beforeTabSwitchCallbackRef = useRef(null);
  // Callback ref for tab close events (GraphContext can register to cleanup subscriptions)
  const onTabCloseCallbackRef = useRef(null);

  // Get active tab object
  const activeTab = useMemo(() => {
    return tabs.find(tab => tab.id === activeTabId) || null;
  }, [tabs, activeTabId]);

  // Check if we should show tab warning
  const shouldShowTabWarning = useMemo(() => {
    return tabs.length >= TAB_WARNING_THRESHOLD && !tabWarningDismissedRef.current;
  }, [tabs.length]);

  /**
   * Apply memory management - compress old tabs
   */
  const applyMemoryManagement = useCallback((tabsList, newActiveId) => {
    if (tabsList.length <= MAX_FULL_DATA_TABS) {
      return tabsList;
    }

    // Sort by lastAccessedAt descending
    const sortedByAccess = [...tabsList].sort((a, b) => b.lastAccessedAt - a.lastAccessedAt);

    // Keep full data for most recent tabs, compress the rest
    const recentTabIds = new Set(
      sortedByAccess.slice(0, MAX_FULL_DATA_TABS).map(t => t.id)
    );
    // Always keep active tab uncompressed
    if (newActiveId) {
      recentTabIds.add(newActiveId);
    }

    return tabsList.map(tab => {
      if (recentTabIds.has(tab.id)) {
        return tab;
      }
      return compressTab(tab);
    });
  }, []);

  /**
   * Register a callback to be called before tab switch to save current state
   */
  const registerBeforeTabSwitchCallback = useCallback((callback) => {
    beforeTabSwitchCallbackRef.current = callback;
  }, []);

  /**
   * Register a callback to be called when a tab is being closed
   * Allows external consumers to perform synchronous cleanup (e.g., unsubscribe websocket)
   */
  const registerOnTabCloseCallback = useCallback((callback) => {
    onTabCloseCallbackRef.current = callback;
  }, []);

  /**
   * Create a new tab
   */
  const createTab = useCallback((searchTerm = '', options = {}) => {
    // Check if max tabs reached
    if (tabs.length >= MAX_TABS) {
      // User attempted to create but max reached -> show creation warning
      setShowTabWarningOnCreate(true);
      return null;
    }

    // IMPORTANT: Save current tab's graph state BEFORE switching
    // This must happen synchronously before React re-renders
    if (activeTabId && beforeTabSwitchCallbackRef.current) {
      beforeTabSwitchCallbackRef.current(activeTabId);
    }

    const newTab = createTabObject(searchTerm, options);

    setTabs(prevTabs => {
      // Double-check in case of race condition
      if (prevTabs.length >= MAX_TABS) {
        return prevTabs;
      }

      const updatedTabs = [...prevTabs, newTab];

      // Apply memory management
      return applyMemoryManagement(updatedTabs, newTab.id);
    });

    setActiveTabId(newTab.id);

    // Show creation warning if threshold reached (1 tab before max)
    // Keep this outside setTabs updater to avoid render-phase router warnings.
    const projectedTabCount = tabs.length + 1;
    if (projectedTabCount >= TAB_WARNING_THRESHOLD && !tabWarningDismissedRef.current) {
      setShowTabWarningOnCreate(true);
    }

    navigate(buildTabProcessViewPath(newTab));

    return newTab.id;
  }, [navigate, applyMemoryManagement, tabs.length, activeTabId]);

  /**
   * Create tab from attribute click (passes to AI agent)
   */
  const createTabFromAttribute = useCallback((attributeValue, parentTabId) => {
    return createTab(attributeValue, {
      source: 'attribute-click',
      parentTabId
    });
  }, [createTab]);

  /**
   * Create tab from Session Summary row action.
   */
  const createTabFromSummary = useCallback((question, parentTabId) => {
    return createTab(question, {
      source: 'session-summary',
      parentTabId
    });
  }, [createTab]);

  /**
   * Close a tab
   */
  const closeTab = useCallback((tabId) => {
    const tabExists = tabs.some(t => t.id === tabId);
    const nextCount = tabExists ? Math.max(0, tabs.length - 1) : tabs.length;

    // Let any registered consumer clean up synchronously (subscriptions, timers, etc.)
    try {
      onTabCloseCallbackRef.current?.(tabId);
    } catch { /* swallow */ }

    setTabs(prevTabs => {
      const tabIndex = prevTabs.findIndex(t => t.id === tabId);
      if (tabIndex === -1) return prevTabs;

      const newTabs = prevTabs.filter(t => t.id !== tabId);

      // If closing the active tab, switch to another
      if (tabId === activeTabId) {
        if (newTabs.length === 0) {
          // No tabs left, navigate to search
          setActiveTabId(null);
          navigate(SEARCH_PATH);
        } else {
          // Switch to the tab before, or the first tab
          const newActiveIndex = Math.max(0, tabIndex - 1);
          const newActiveTab = newTabs[newActiveIndex];
          setActiveTabId(newActiveTab.id);

          navigate(buildTabProcessViewPath(newActiveTab));
        }
      }
      return newTabs;
    });

    // Hide creation warning if below threshold.
    // Keep this outside setTabs updater to avoid render-phase router warnings.
    if (nextCount < TAB_WARNING_THRESHOLD) {
      setShowTabWarningOnCreate(false);
    }
  }, [activeTabId, navigate, tabs]);

  /**
   * Switch to a different tab
   */
  const switchTab = useCallback((tabId, options = {}) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    const forceNavigate = options?.forceNavigate === true;

    // Don't switch to the same tab.
    // However, when coming from admin pages we may need to force navigation to the process view
    // so the graph container renders (e.g., user clicked the already-active tab in header).
    if (tabId === activeTabId) {
      if (forceNavigate) {
        navigate(buildTabProcessViewPath(tab));
      }
      return;
    }

    // IMPORTANT: Save current tab's graph state BEFORE switching
    // This must happen synchronously before React re-renders
    if (activeTabId && beforeTabSwitchCallbackRef.current) {
      beforeTabSwitchCallbackRef.current(activeTabId);
    }

    // Update last accessed time
    setTabs(prevTabs => {
      const updatedTabs = prevTabs.map(t => {
        if (t.id === tabId) {
          return { ...t, lastAccessedAt: Date.now() };
        }
        return t;
      });
      return applyMemoryManagement(updatedTabs, tabId);
    });

    setActiveTabId(tabId);

    navigate(buildTabProcessViewPath(tab));
  }, [tabs, navigate, applyMemoryManagement, activeTabId]);

  /**
   * Update a tab's state
   */
  const updateTabState = useCallback((tabId, updates) => {
    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        if (tab.id !== tabId) return tab;

        // tab updated

        // Handle nested updates for graphStates
        if (updates.graphStates) {
          return {
            ...tab,
            ...updates,
            graphStates: {
              ...tab.graphStates,
              ...updates.graphStates
            },
            // Mark as uncompressed since we're updating data
            isCompressed: false
          };
        }

        return {
          ...tab,
          ...updates,
          isCompressed: false
        };
      });
    });
  }, []);

  /**
   * Get a tab's state
   */
  const getTabState = useCallback((tabId) => {
    return tabs.find(t => t.id === tabId) || null;
  }, [tabs]);

  /**
   * Update the search term for a tab (updates both searchTerm and displayName)
   */
  const updateTabSearchTerm = useCallback((tabId, searchTerm) => {
    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        if (tab.id !== tabId) return tab;
        return {
          ...tab,
          searchTerm,
          displayName: generateTabDisplayName(searchTerm),
          searchValue: searchTerm
        };
      });
    });
  }, []);

  /**
   * Dismiss the tab warning
   */
  const dismissTabWarning = useCallback(() => {
    tabWarningDismissedRef.current = true;
    setShowTabWarningOnCreate(false);
  }, []);

  /**
   * Close all tabs
   */
  const closeAllTabs = useCallback(() => {
    setTabs([]);
    setActiveTabId(null);
    navigate(SEARCH_PATH);
  }, [navigate]);

  /**
   * Check if a tab needs to be refreshed (was compressed)
   */
  const isTabCompressed = useCallback((tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    return tab?.isCompressed || false;
  }, [tabs]);

  /**
   * Get all tabs' graph data for admin page merging
   * Returns an array of graph data objects from all tabs (not compressed)
   * Each contains { nodes: [...], edges: [...] }
   */
  const getAllTabsGraphData = useCallback(() => {
    return tabs
      .filter(tab => !tab.isCompressed && tab.savedGraphState?.graphStates)
      .map(tab => {
        const graphStates = tab.savedGraphState.graphStates;
        // Get the current mode's graph data (prefer updatedData over data)
        const mode = tab.savedGraphState.currentMode || GRAPH_MODES.BASIC;
        const state = graphStates[mode];
        return state?.updatedData || state?.data || null;
      })
      .filter(Boolean);
  }, [tabs]);

  /**
   * Navigate to search page (for + button)
   */
  const openNewTabSearch = useCallback(() => {
    navigate(SEARCH_PATH);
  }, [navigate]);

  /**
   * Navigate to the graph view from an admin/contextual action.
   * If there are existing tabs, switch to the active/first tab so
   * the registered before-switch callback runs (synchronously saving state).
  * Otherwise navigate to the process view with optional stored search params.
   */
  const openGraphFromAdmin = useCallback((storedSearchString = '') => {
    if (tabs.length > 0) {
      const targetTab = tabs.find(t => t.id === activeTabId) || tabs[0];
      if (targetTab) {
        // Use switchTab so the synchronous before-switch callback fires
        switchTab(targetTab.id, { forceNavigate: targetTab.id === activeTabId });
        return;
      }
    }

    // No tabs - navigate to graph with stored search string (if any)
    if (storedSearchString) {
      navigate(`${PROCESS_VIEW_PATH}?${storedSearchString}`);
    } else {
      navigate(PROCESS_VIEW_PATH);
    }
  }, [tabs, activeTabId, switchTab, navigate]);

  const contextValue = useMemo(() => ({
    // State
    tabs,
    activeTabId,
    activeTab,
    showTabWarningOnCreate,
    shouldShowTabWarning,
    tabCount: tabs.length,
    maxTabsReached: tabs.length >= MAX_TABS,

    // Actions
    createTab,
    createTabFromAttribute,
    createTabFromSummary,
    closeTab,
    switchTab,
    registerOnTabCloseCallback,
    updateTabState,
    getTabState,
    updateTabSearchTerm,
    dismissTabWarning,
    closeAllTabs,
    isTabCompressed,
    openNewTabSearch,
    openGraphFromAdmin,
    registerBeforeTabSwitchCallback,
    getAllTabsGraphData,

    // Constants
    MAX_TABS,
    MAX_TAB_DISPLAY_LENGTH,
    TAB_WARNING_THRESHOLD,
  }), [
    tabs,
    activeTabId,
    activeTab,
    showTabWarningOnCreate,
    shouldShowTabWarning,
    createTab,
    createTabFromAttribute,
    createTabFromSummary,
    closeTab,
    switchTab,
    registerOnTabCloseCallback,
    updateTabState,
    getTabState,
    updateTabSearchTerm,
    dismissTabWarning,
    closeAllTabs,
    isTabCompressed,
    openNewTabSearch,
    openGraphFromAdmin,
    registerBeforeTabSwitchCallback,
    getAllTabsGraphData
  ]);


  return (
    <TabContext.Provider value={contextValue}>
      {children}
    </TabContext.Provider>
  );
};

export default TabContext;
